import { DealbreakerQuestion, DealbreakerAnswer } from "@/types/dealbreaker";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface DealbreakerAnswersViewProps {
  myQuestions: DealbreakerQuestion[];
  theirAnswersToMyQuestions: DealbreakerAnswer[];
  theirQuestions: DealbreakerQuestion[];
  myAnswersToTheirQuestions: DealbreakerAnswer[];
  myProfileId: string;
  theirProfileId: string;
}

export function DealbreakerAnswersView({
  myQuestions,
  theirAnswersToMyQuestions,
  theirQuestions,
  myAnswersToTheirQuestions,
  myProfileId,
  theirProfileId,
}: DealbreakerAnswersViewProps) {
  // Don't render if no questions from either side
  if (myQuestions.length === 0 && theirQuestions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <Separator />
      <h2 className="text-2xl font-bold">Dealbreaker Questions & Answers</h2>

      {/* Their Answers to My Questions */}
      {myQuestions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Your Questions & Their Answers</CardTitle>
            <CardDescription>
              Here are the answers they provided to your dealbreaker questions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {myQuestions.map((question, index) => {
              const answer = theirAnswersToMyQuestions.find(
                (a) => a.question_id === question.id && a.answered_by_profile_id === theirProfileId
              );

              return (
                <div key={question.id} className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-primary text-sm">Q{index + 1}:</span>
                    <p className="text-sm font-medium flex-1">{question.question_text}</p>
                  </div>
                  <div className="flex items-start gap-2 ml-6">
                    <span className="font-semibold text-muted-foreground text-sm">A:</span>
                    <p className="text-sm text-muted-foreground flex-1">
                      {answer?.answer_text || <em className="text-destructive">No answer provided</em>}
                    </p>
                  </div>
                  {index < myQuestions.length - 1 && <Separator className="mt-4" />}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* My Answers to Their Questions */}
      {theirQuestions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Their Questions & Your Answers</CardTitle>
            <CardDescription>
              Here are your answers to their dealbreaker questions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {theirQuestions.map((question, index) => {
              const answer = myAnswersToTheirQuestions.find(
                (a) => a.question_id === question.id && a.answered_by_profile_id === myProfileId
              );

              return (
                <div key={question.id} className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-primary text-sm">Q{index + 1}:</span>
                    <p className="text-sm font-medium flex-1">{question.question_text}</p>
                  </div>
                  <div className="flex items-start gap-2 ml-6">
                    <span className="font-semibold text-muted-foreground text-sm">A:</span>
                    <p className="text-sm text-muted-foreground flex-1">
                      {answer?.answer_text || <em className="text-destructive">No answer provided</em>}
                    </p>
                  </div>
                  {index < theirQuestions.length - 1 && <Separator className="mt-4" />}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
